import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { broadcastService } from 'src/common/services/broadcastService';
import {
  Box,
  styled,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  TextField
} from '@mui/material';
import StepConnector, {
  stepConnectorClasses
} from '@mui/material/StepConnector';
import {
  runSkipBtn,
  addSiteInDashboardText,
  checkKeyword,
  selectCountry,
  siteAuditModal,
  IndexedTutorial,
  scanPageRestriction,
  siteAuditModalText,
  paths,
  siteAuditLinks,
  planNames
} from 'src/common/config/TextMessage';
import { StepIconProps } from '@mui/material/StepIcon';
import { getHtml, getSiteAuditUrl, runAuditUrls, siteDetail } from '../../common/Scraper';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import {
  checkValidURL,
  localStorageMethod,
  openNewTab,
  setRequestGscURL,
  siteUrlFormat,
  updateGSCText
} from 'src/common/config/FunctionHelper';
import { country } from 'src/common/config/Country';
import Links from '@mui/material/Link';
import InfoIcon from '@mui/icons-material/Info';
import {
  setAuditSiteDetails,
  setIsOpenChangePlan,
  setOpenReAuditModal,
  setRestrictReAuditModal,
  setSiteAuditRefresh,
  setSiteAuditSite,
  setSiteId,
  setSquareSpaceCode
} from 'src/redux/siteAuditReducer';
import { Add } from '@mui/icons-material';
import CheckboxList from 'src/common/CheckBox';
import { siteAuditModalSx } from './siteAuditCSS';
import CommonModal from 'src/common/config/CommonModal';
import CommonAlert from 'src/common/CommonAlert';
import { useLocation, useNavigate } from 'react-router';
import { SkipSiteAuditModal } from './SkipSiteAuditModal';

/**
 * Styled component for the Qonto step connector.
 * @param {object} theme - The theme object.
 * @returns {object} The styled component.
 */
const QontoConnector = styled(StepConnector)(({ theme }) => ({
  // Styling for alternative label step connector
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)'
  },
  // Styling for active step connector
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--color-secondary)'
    }
  },
  // Styling for completed step connector
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: 'var(--color-secondary)'
    }
  },
  // Styling for the step connector line
  [`& .${stepConnectorClasses.line}`]: {
    borderColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderWidth: 2,
    borderRadius: 1,
    borderStyle: 'dashed'
  }
}));

/**
 * Styled component for the Colorlib step icon.
 * @param {object} props - The step icon props.
 * @returns {object} The styled component for the step icon.
 */
const ColorlibStepIconRoot = styled(Box)<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : 'var(--color-border-light2)',
  zIndex: 1,
  color: 'var(--color-white)',
  width: 25,
  height: 25,
  display: 'flex',
  paddingBottom: 2.5,
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundColor: 'var(--color-primary)'
  }),
  ...(ownerState.completed && {
    backgroundColor: 'var(--color-secondary)'
  })
}));

/**
 * ColorlibStepIcon component represents the step icon in the Colorlib style.
 * @param {object} props - The step icon props.
 * @returns {object} The Colorlib step icon component.
 */
const ColorlibStepIcon = (props: StepIconProps) => {
  const { active, completed, className } = props;

  const icons = {
    1: 1,
    2: 2,
    3: 3,
    4: 4
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
};

/**
 * SiteAudit modal component.
 * @param {object} param0 - Props for the SiteAuditModal.
 * @param {Function} param0.handleAddScanModalClose - Function to close the modal.
 * @returns {JSX.Element} - SiteAudit modal HTML.
 */
const SiteAuditModal = ({ handleAddScanModalClose }) => {
  const steps = ['Add Website', 'Skip pages', 'GSC Connection', 'Tutorial'];
  const [activeStep, setActiveStep] = React.useState(0);
  const [openAdvanceSetting, setOpenAdvanceSetting] = useState(false);
  const [countryData, setCountryData] = useState<any>(country[0]);
  const [successSite, setSuccessSite] = useState(null);
  const [errorSite, setErrorSite] = useState(null);
  const [getUrlText, setGetUrlText] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [planName, setPlanName] = useState('');
  const [auditUrls, setAuditUrls] = useState([]);
  const [unSelectedUrls, setUnSelectedUrls] = useState([]);
  const [selectedAuditUrls, setSelectedAuditUrls] = useState(auditUrls);
  const [googleAuthText, setGoogleAuth] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(true);
  const [siteAuditSuccess, setSiteAuditSuccess] = useState('');
  const [siteAuditError, setSiteAuditError] = useState('');
  const [selectedUrlNumber, setSelectedUrlsNumber] = useState(0);
  const [isDraft, setIsDraft] = useState(false);
  const [openUpdateFieldsModal, setOpenUpdateFieldsModal] = useState(false);
  const [showLimitText, setShowLimitText] = useState(false);
  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const [urldifference, setUrldifference] = useState(0);
  const [limitAuditUrl, setLimitAuditUrl] = useState(0);
  const [restrictPlanMsg, setRestirctPlanMsg] = useState(null);
  const [gscStatus, setGscStatus] = useState(null);
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [isReAuditError, setIsReAuditError] = useState(false);
  const [isLimitExceed, setIsLimitExceed] = useState(false);
  const [openSkipModal, setOpenSkipModal] = useState(false);
  const dispatch = useDispatch();
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let selectedSiteSqSp = localStorageMethod('get', 'squareSpaceSite');
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isDiyOrFreePlan =
    activePlanName === planDetails?.starterPlus ||
    activePlanName === planDetails?.diy ||
    activePlanName === planDetails?.free;
  const isProPlan =
    activePlanName === planDetails?.designer ||
    activePlanName === planDetails?.agency ||
    activePlanName === planDetails?.expertPlus;
  useEffect(() => {
    broadcastService.subscribe('site-audit-modal', {
      'gsc_auth_connection': (data) => {
        setGoogleAuth(data);
      }
    });
    return () => broadcastService.unsubscribe('site-audit-modal');
  }, []);

  useEffect(() => {
    if (selectedSiteSqSp) {
      setGetUrlText(selectedSiteSqSp);
      setButtonDisable(false);
    }
  }, [selectedSiteSqSp]);

  const navigate = useNavigate();
  const location = useLocation();

  const auditSiteDetails = useSelector(
    (state: RootState) => state.siteAudit.auditSiteDetails
  ) ?? [];

  const getCleanUrl = (url: string) => {
    return url?.replace(/^https?:\/\//, '')?.replace(/^www\./, '')?.replace(/\/$/, '');
  };

  const isCurrentSiteDeleted =
    auditSiteDetails?.some((site: any) => {
      const isUrlMatch = getUrlText && getCleanUrl(getUrlText) === getCleanUrl(site?.url);
      return isUrlMatch && site?.isDelete;
    }) || false;

  let userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );

  useEffect(() => {
    setPlanName(activePlanName);
    return () => {
      setPlanName('');
    };
  }, [planName, googleAuthText]);

  /**
   * Handle empty error messages after a certain time.
   */
  const emptyErrorMessage = (isReAuditErrorFlag = false) => {
    if (isReAuditErrorFlag) {
      return;
    }
    setTimeout(() => {
      setErrorSite('');
      setSuccessSite('');
      setSiteAuditSuccess('');
      setSiteAuditError('');
      setLoading(false);
    }, 5000);
  };

  /**
   * Handle the event when a site is added.
   */
  const handledSite = async () => {
    setLoading(true);
    let url = getUrlText;
    let keywordCheck = keyword;
    let checkCountry = countryData?.locationId;

    // Url is not entered, give an error
    if (!url) {
      setErrorSite(addSiteInDashboardText.notAddsite);
      emptyErrorMessage();
      return;
    }

    // Url is not in proper format, give an error
    if (!url.match(addSiteInDashboardText.urlRegex)) {
      setErrorSite(addSiteInDashboardText.notValidUrl);
      emptyErrorMessage();
      return;
    }

    if (!url.match(addSiteInDashboardText.urlValidation)) {
      setErrorSite(addSiteInDashboardText.notValidUrl);
      emptyErrorMessage();
      return;
    }

    if (openAdvanceSetting) {
      // Keyword is not entered, give an error
      if (!keywordCheck) {
        setErrorSite(checkKeyword);
        emptyErrorMessage();
        return;
      }

      // Check country is not entered, give an error
      if (!checkCountry) {
        setErrorSite(selectCountry);
        emptyErrorMessage();
        return;
      }
    }

    // Make the URL in the proper format
    let enteredURL = url;
    if (url.indexOf('http:') < 0 && url.indexOf('https:') < 0) {
      url = 'https://' + url;
      enteredURL = url;
    }

    let titleName = '';
    let uinqueId = '';

    // Get HTML from the URL
    const payload = {
      url: url
    };

    let getHtmlData = await getHtml(payload);
    const responseDetails: any = getHtmlData;
    if (isDraft == false) {
      if (responseDetails.status == 200) {
        if (responseDetails?.data?.data) {
          const documentElement: any = responseDetails.data.data;
          // Get unique id
          uinqueId = documentElement?.uniqueId;
          // uinqueId = getUniqId(documentElement);
          if (!uinqueId) {
            setErrorSite(addSiteInDashboardText.notSquarespaceUrl);
            emptyErrorMessage();
            return;
          }
          titleName = documentElement?.title;

          url = documentElement?.url;
          // Check if the URL is valid or not
          if (!checkValidURL(url)) {
            url = enteredURL;
          }
        } else {
          // This is the draft error message commented code
          // setErrorSite(addSiteInDashboardText.pageRemovedDraftMsg);
          setErrorSite(responseDetails?.data?.message);
          emptyErrorMessage();
          return;
        }
      } else {
        setErrorSite(
          responseDetails?.status == 404 && isDiyOrFreePlan
            ? addSiteInDashboardText.page404ErrorFreeOrDiy
            : responseDetails?.status == 404 && isProPlan
            ? addSiteInDashboardText?.page404ErrorPro
            : addSiteInDashboardText.notSquarespaceUrl
        );
        emptyErrorMessage();
        return;
      }
    }
    let requestPayload;

    requestPayload = {
      url: url,
      title: titleName,
      uniqueId: uinqueId,
      siteAudit: true
    };

    if (isDraft === true) {
      requestPayload = {
        url: url,
        title: titleName !== '' ? titleName : siteUrlFormat(url),
        uniqueId: uinqueId !== '' ? uinqueId : siteUrlFormat(url),
        isDrafted: isDraft,
        siteAudit: true
      };
    }
    let auditResponse: any = await getSiteAuditUrl(requestPayload);

    if (isDraft == true) {
      if (auditResponse?.status === 200) {
        setSiteAuditSuccess(auditResponse?.data?.message);
        dispatch(setSiteAuditRefresh(true));
        setTimeout(() => {
          handleAddScanModalClose();
          setLoading(false);
        }, 5000);
      } else {
        setErrorSite(auditResponse.data.message);
        emptyErrorMessage();
        return;
      }
    } else {
      if (auditResponse?.status === 200) {
        setSiteAuditSuccess(auditResponse?.data?.message);
        if (auditResponse?.data?.limitUrl > 0) {
          setLimitAuditUrl(auditResponse?.data?.limitUrl);
        }
        setGscStatus(auditResponse?.data?.isGsc);
        setAuditUrls(auditResponse?.data?.response);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        emptyErrorMessage();
      } else {
        let errorMessage = auditResponse.data.message;
        let reAuditError = auditResponse?.data?.reAudit;
        let isReAuditErrorFlag = false;
        if (reAuditError) {
          isReAuditErrorFlag = true;
          if (auditResponse?.data?.siteId) {
            let siteDetailResponse: any = await siteDetail(auditResponse?.data?.siteId, null);
            if (siteDetailResponse?.response) {
              if (siteDetailResponse?.response?.isDelete) {
                errorMessage = "This website is already deleted, you cannot re-audit it.";
                reAuditError = false;
                isReAuditErrorFlag = false;
              }
            }
          }
        }
        setErrorSite(errorMessage);
        setIsReAuditError(reAuditError);
        setIsLimitExceed(auditResponse?.data?.exceedScan);
        emptyErrorMessage(isReAuditErrorFlag);
        return;
      }
    }
  };

  /**
   * Handle the selection of audit URLs.
   * @param {array} urls - Selected audit URLs.
   */
  const selectedValues = (urls) => {
    setSelectedUrlsNumber(urls.length);
    setSelectedAuditUrls(urls);
    if (limitAuditUrl !== 0 || urls.length >= limitAuditUrl) {
      setIsButtonDisable(false);
    } else {
      setIsButtonDisable(true);
    }
    const filteredUrls = auditUrls.filter((url) => !urls.includes(url));
    setUnSelectedUrls(filteredUrls);
    let difference = auditUrls.length - urls.length;
    setUrldifference(difference);

    if (activePlanName !== planNames.agency && activePlanName !== planNames.expertPlus) {
      if (scanPageRestriction[activePlanName]?.pages < difference) {
        const finalMsg =
          scanPageRestriction[activePlanName].msg1 +
          (difference - scanPageRestriction[activePlanName]?.pages) +
          scanPageRestriction[activePlanName].msg2;
        setRestirctPlanMsg(finalMsg);
        setIsButtonDisable(true);
      } else {
        if (limitAuditUrl !== 0 && difference >= limitAuditUrl) {
          setIsButtonDisable(true);
        } else {
          setIsButtonDisable(false);
        }
        setRestirctPlanMsg(null);
      }
    }
  };
  /**
   * Handle the event when an audit is run on a site.
   */
  const runAuditSite = async () => {
    setLoading(true);
    let url = getUrlText;
    let keywordCheck = keyword;
    let checkCountry = countryData?.locationId;

    // Url is not entered, give an error
    if (!url) {
      setErrorSite(addSiteInDashboardText.notAddsite);
      emptyErrorMessage();
      return;
    }

    // Url is not in proper format, give an error
    if (!url.match(addSiteInDashboardText.urlRegex)) {
      setErrorSite(addSiteInDashboardText.notValidUrl);
      emptyErrorMessage();
      return;
    }

    if (openAdvanceSetting) {
      // Keyword is not entered, give an error
      if (!keywordCheck) {
        setErrorSite(checkKeyword);
        emptyErrorMessage();
        return;
      }

      // Check country is not entered, give an error
      if (!checkCountry) {
        setErrorSite(selectCountry);
        emptyErrorMessage();
        return;
      }
    }

    // Make the URL in the proper format
    let enteredURL = url;
    if (url.indexOf('http:') < 0 && url.indexOf('https:') < 0) {
      url = 'https://' + url;
      enteredURL = url;
    }

    let titleName = '';
    let uinqueId = '';

    // Get HTML from the URL
    const payload = {
      url: url
    };

    let getHtmlData = await getHtml(payload);
    const responseDetails: any = getHtmlData;
    if (responseDetails.status == 200) {
      // if (responseDetails?.data?.data) {
      if (responseDetails?.data) {
        const documentElement: any = responseDetails.data.data;
        // Get unique id
        // uinqueId = getUniqId(documentElement);
        uinqueId = documentElement?.uniqueId;
        if (!uinqueId) {
          setErrorSite(addSiteInDashboardText.notSquarespaceUrl);
          emptyErrorMessage();
          return;
        }
        titleName = documentElement?.title;

        url = documentElement?.url;

        // Check if the URL is valid or not
        if (!checkValidURL(url)) {
          url = enteredURL;
        }
      } else {
        setErrorSite(addSiteInDashboardText.pageRemovedDraftMsg);
        emptyErrorMessage();
        return;
      }
    } else {
      setErrorSite(addSiteInDashboardText.notSquarespaceUrl);
      emptyErrorMessage();
      return;
    }

    let requestPayload = {
      url: url,
      title: titleName,
      uniqueId: uinqueId,
      urls: unSelectedUrls,
      skippedUrls: selectedAuditUrls
      // skippedUrls: []
    };

    let auditResponse: any = await runAuditUrls(requestPayload);
    if (auditResponse?.status === 200) {
      const cleanUrl = (
        url?.replace(/^https?:\/\//, '') ||
        requestPayload?.url?.replace(/^https?:\/\//, '')
      )?.replace(/\/$/, '');
      const newSiteId = auditResponse?.data?.siteId;
      if (cleanUrl && newSiteId) {
        let values = [cleanUrl, newSiteId];
        localStorage.setItem('lastSelectedSite', JSON.stringify(values));
        dispatch(setSiteId(newSiteId));
      }
      dispatch(setAuditSiteDetails([]));
      localStorageMethod('remove', 'squareSpaceSite');
      localStorageMethod('remove', 'squareSpaceSiteId');
      localStorageMethod('remove', 'squareSpaceAccountId');
      localStorageMethod('remove', 'reaudit_process');
      setSiteAuditSuccess(auditResponse?.data?.message);
      dispatch(setSiteAuditRefresh(true));
      setTimeout(() => {
        if (userDetails?.role_type !== 'member' || !userDetails?.role_type) {
          //if (
          //  (!userDetails?.team_size && userDetails?.team_size !== '') ||
          //  (!userDetails?.your_role && userDetails?.your_role !== '')
          //) {
          //  setOpenUpdateFieldsModal(true);
          //}
          //else {
          handleAddScanModalClose();
          setLoading(false);
          //}
        } else {
          handleAddScanModalClose();
          setLoading(false);
        }
      }, 5000);
    } else {
      setSiteAuditError(auditResponse?.data?.message);
      setTimeout(() => {
        setLoading(false);
        emptyErrorMessage();
        handleSkipModal();
      }, 5000);
    }
  };

  /**
   * Handle the change in the URL input field.
   * @param {object} e - The event object.
   */
  const handleUrlChange = (e) => {
    // const newUrl = e.target.value;
    const newUrl = e?.target?.value?.trim();
    // const newUrl = selectedSiteSqSp ? selectedSiteSqSp : e.target.value.trim();
    setGetUrlText(newUrl);
    // Url is not in proper format, disable the button and give an error
    if (!newUrl?.match(addSiteInDashboardText?.urlValidation)) {
      setButtonDisable(true);
      setErrorSite(addSiteInDashboardText?.notValidUrl);
      return;
    }
    setButtonDisable(false);
    setTimeout(() => {
      setErrorSite('');
    }, 1000);
  };

  /**
   * Handle moving to the next step in the Site Audit modal.
   */
  const handleNext = () => {
    if (activeStep < 5) {
      if (activeStep === 0) {
        dispatch(setRestrictReAuditModal(true));
        handledSite();
        return;
      }
      if (activeStep == 2 && !gscStatus && !googleAuthText?.success) {
        openNewTab(
          'https://www.youtube.com/watch?v=7bKsHsi7op0&list=PLlaoclvOMRPyFA6ylbOSMH3-0Ah3txns4'
        );
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
      }

      if (activeStep == 2 && (googleAuthText?.success || gscStatus)) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else if (activeStep < 3) {
        if (
          activeStep == 1 &&
          urldifference > 100 &&
          selectedUrlNumber !== auditUrls?.length
        ) {
          setShowLimitText(true);
          setLoading(true);
          setTimeout(() => {
            setLoading(false);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
          }, 5000);
        } else if (activeStep == 1 && selectedUrlNumber == auditUrls?.length) {
          setShowAllSelected(true);
          setLoading(true);
          setTimeout(() => {
            setLoading(false);
            setShowAllSelected(false);
          }, 5000);
        } else {
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
      }

      if (activeStep == 3) {
        runAuditSite();
      }
    }
  };

  /**
   * Handle skipping to the next step in the Site Audit modal.
   */
  const handleNextSkip = () => {
    if (activeStep < 5) {
      if (activeStep == 2) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }

      if (activeStep !== 2) {
        if (selectedSiteSqSp) {
          setOpenSkipModal(true);
        } else {
          handleAddScanModalClose();
        }
      }
    }
  };

  /**
   * Get the guide text based on the active step.
   * @returns {string} The guide text.
   */
  const getGuideText = () => {
    switch (activeStep) {
      case 0:
        return siteAuditModal?.siteWide;
      case 1:
        return siteAuditModal?.removePages;
      case 2:
        return '';
      case 3:
        return '';
    }
  };

  const handleCloseUpdateFieldsModal = () => {
    setOpenUpdateFieldsModal(false);
  };

  const handleTheExceedLimit = () => {
    dispatch(setIsOpenChangePlan(true));
    navigate(paths?.billing);
    handleAddScanModalClose();
  };

  const handleReAuditError = () => {
    if (isCurrentSiteDeleted) {
      return;
    }
    if (location?.pathname !== paths?.dashboard) {
      navigate(paths?.dashboard);
    }
    dispatch(setSiteAuditSite(getUrlText));
    setErrorSite('');
    handleAddScanModalClose();
    localStorageMethod('remove', 'squareSpaceSite');
    localStorageMethod('remove', 'squareSpaceSiteId');
    localStorageMethod('remove', 'squareSpaceAccountId');
    dispatch(setOpenReAuditModal(true));
  };

  const handleSkipModal = () => {
    setOpenSkipModal(false);
    handleAddScanModalClose();
    dispatch(setSquareSpaceCode(null));
    localStorageMethod('remove', 'squareSpaceSite');
    localStorageMethod('remove', 'squareSpaceSiteId');
    localStorageMethod('remove', 'squareSpaceAccountId');
  };

  const handleCloseSkipModal = () => {
    setOpenSkipModal(false);
  };

  /**
   * Get the HTML content for the current step of the Site Audit modal.
   *
   * @returns {JSX.Element} - HTML content for the current step.
   */
  const getAuditModalSectionHtml = () => {
    switch (activeStep) {
      case 0:
        return (
          <Fragment>
            {/* Step 0: Add Website */}
            <div className="flex-col">
              <Typography sx={siteAuditModalSx?.siteAuditModalDescription}>
                {/* {siteAuditModalDescription} */}
              </Typography>
            </div>
            <div className="flex-col my-3 space-y-3 mb-0">
              {/* Display info alert */}
              <Alert
                severity="info"
                icon={<InfoIcon style={siteAuditModalSx?.infoIconSx} />}
                sx={siteAuditModalSx?.alertDescription}
                variant="filled"
              >
                <span>{siteAuditModal?.alertDescription}</span>
              </Alert>
            </div>
            <div className="flex-col my-3 space-y-3 mb-0">
              {/* Input field for URL */}
              <TextField
                value={getUrlText}
                defaultValue={getUrlText}
                variant="filled"
                label="Enter the URL of your homepage"
                InputProps={{ disableUnderline: true }}
                disabled={selectedAuditUrls?.length > 0}
                size="small"
                className="w-full"
                onChange={handleUrlChange}
              ></TextField>
            </div>

            <Stack spacing={0} mt={1} mb={2}>
              {/* Display success and error messages */}
              {siteAuditSuccess && isDraft == true && (
                <CommonAlert severity="success" message={siteAuditSuccess} />
              )}
              {siteAuditError && isDraft == true && (
                <CommonAlert severity="error" message={siteAuditError} />
              )}

              {successSite && (
                <CommonAlert severity="success" message={successSite} />
              )}

              {errorSite && !isReAuditError && !isLimitExceed && (
                <CommonAlert severity="error" message={errorSite} />
              )}
              {errorSite && isLimitExceed && (
                <CommonAlert
                  severity="error"
                  message={
                    <Typography
                      sx={{
                        fontSize: 'var(--font-size-14)',
                        lineHeight: 'var(--line-height-20)',
                        fontFamily: 'Inter'
                      }}
                    >
                      {siteAuditModal?.exceedLimitText}{' '}
                      <span
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        onClick={handleTheExceedLimit}
                      >
                        {'upgrade'}
                      </span>{' '}
                      {siteAuditModal?.exceedLimitTextEnd}
                    </Typography>
                  }
                />
              )}
              {errorSite && isReAuditError && (
                <CommonAlert
                  severity="error"
                  message={
                    isCurrentSiteDeleted ? (
                      <Typography
                        sx={{
                          fontSize: 'var(--font-size-14)',
                          lineHeight: 'var(--line-height-20)',
                          fontFamily: 'Inter'
                        }}
                      >
                        {errorSite}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 'var(--font-size-14)',
                          lineHeight: 'var(--line-height-20)',
                          fontFamily: 'Inter'
                        }}
                      >
                        {siteAuditModal?.reAuditErrorFirst}{' '}
                        <span
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          onClick={handleReAuditError}
                        >
                          {siteAuditModal?.reAuditErrorLinkText}
                        </span>{' '}
                        {siteAuditModal?.reAuditErrorSec}
                      </Typography>
                    )
                  }
                />
              )}
            </Stack>
          </Fragment>
        );

      case 1:
        return (
          <Fragment>
            {/* Step 1: Remove pages */}
            <div className="flex-col">
              <Typography sx={siteAuditModalSx?.lorem}>
                {siteAuditModal?.lorem}{' '}
                <>
                  <a
                    href={siteAuditLinks?.watchTutorialLink}
                    className="watch_tutorial_link"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    {siteAuditModalText?.watchThisTutorial}
                  </a>
                </>
                {siteAuditModalText?.morePagesToSkip}
              </Typography>
            </div>
            <div className="flex-col my-3 space-y-3 mb-0">
              <div>
                {auditUrls.length > 0 && (
                  // Display the list of audit URLs
                  <CheckboxList
                    data={auditUrls}
                    selectedValues={selectedValues}
                  />
                )}
              </div>
            </div>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: '400 !important',
                lineHeight: 'var(--line-height-16)',
                letterSpacing: '0em',
                textAlign: 'left',
                color: 'var(--color-primary)',
                paddingTop: '10px'
              }}
            >
              {`${selectedUrlNumber} out of ${auditUrls?.length} URLs skipped`}
            </Typography>
            {showLimitText && (
              <CommonAlert
                severity="error"
                message={siteAuditModalText?.limitText}
              />
            )}
            {showAllSelected && (
              <Alert severity="error">
                {' '}
                <span>
                  You have not included any pages in your site-wide audit.
                  Please only select pages you want to skip from the audit.{' '}
                  <a
                    href={siteAuditLinks?.watchTutorialLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: 'underline',
                      color: 'var(--color-primary)'
                    }}
                  >
                    {siteAuditModalText?.watchThisTutorial}
                  </a>{' '}
                  to learn more.
                </span>
              </Alert>
            )}
            {restrictPlanMsg !== null && isButtonDisable && (
              <CommonAlert severity="error" message={restrictPlanMsg} />
            )}
            {isButtonDisable && restrictPlanMsg === null && (
              <CommonAlert
                severity="error"
                message={siteAuditModalText?.serverHighTraffic}
              />
            )}
            {successSite ||
              (errorSite && (
                <Stack spacing={0} mt={1} mb={2}>
                  {successSite && (
                    <CommonAlert severity="success" message={successSite} />
                  )}

                  {errorSite && (
                    <CommonAlert severity="error" message={errorSite} />
                  )}
                </Stack>
              ))}
          </Fragment>
        );

      case 2:
        return (
          <Fragment>
            {/* Step 2: GSC Connection */}
            <div className="flex-col">
              <img
                src={
                  process.env.PUBLIC_URL + '/static/images/icons/NoGscicon.png'
                }
                width="34rem"
                height="100%"
                style={{
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  marginTop: '5%',
                  width: '14rem'
                }}
              />
            </div>

            {gscStatus ? (
              <div className="flex-col my-3 space-y-3 mb-0">
                <Alert severity="success">
                  {siteAuditModal?.gscIsConnectedText}
                </Alert>
              </div>
            ) : (
              <>
                <div className="flex-col my-3 space-y-3 mb-0">
                  <Typography sx={siteAuditModalSx?.lorem}>
                    {siteAuditModal?.gscDescription}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      localStorage.setItem('GSCRequestURL', getUrlText);
                      setRequestGscURL(paths?.google, true);
                    }}
                    startIcon={<Add />}
                    sx={siteAuditModalSx?.googleSearchConsole}
                    disabled={googleAuthText?.success}
                  >
                    {siteAuditModal?.googleSearchConsole}
                  </Button>
                </div>
                <div className="flex-col my-3 space-y-3 mb-0">
                  {googleAuthText && (
                    <Alert
                      severity={googleAuthText?.success ? 'success' : 'error'}
                      sx={{ marginTop: '1rem', width: '100%' }}
                    >
                      <Typography
                        sx={siteAuditModalSx?.updateGSCText}
                        dangerouslySetInnerHTML={{
                          __html: updateGSCText(
                            googleAuthText?.meassage,
                            IndexedTutorial,
                            ''
                          )
                        }}
                      ></Typography>
                    </Alert>
                  )}
                </div>
              </>
            )}

            <div className="flex-col my-3 space-y-3 mb-0">
              <Alert
                severity="info"
                icon={
                  <InfoIcon
                    style={{
                      color: 'var(--color-teal)',
                      fontSize: 'var(--font-size-20)',
                      marginLeft: '10px'
                    }}
                  />
                }
                sx={{
                  height: '0px',
                  visibility: 'hidden'
                }}
                variant="filled"
              >
                <span>{siteAuditModal.alertDescription}</span>
              </Alert>
            </div>
          </Fragment>
        );

      case 3:
        return (
          <Fragment>
            {/* Step 3: Tutorial */}
            <div className="flex-col">
              <Typography style={siteAuditModalSx.lorem}>
                {siteAuditModal.tutorialDescription}
              </Typography>
            </div>
            <div
              className="flex-col my-3 h-64 space-y-3 mb-0"
              style={{ marginTop: '7%' }}
            >
              <iframe
                src={siteAuditModal.progressModuleVideoLink}
                width="100%"
                height="100%"
                style={{
                  borderRadius: '7px',
                  padding: '8px, 12px, 8px, 12px',
                  gap: '8px'
                }}
              ></iframe>
            </div>
            {siteAuditSuccess && (
              <CommonAlert severity="success" message={siteAuditSuccess} />
            )}

            {siteAuditError && (
              <CommonAlert severity="error" message={siteAuditError} />
            )}
          </Fragment>
        );
    }
  };

  return (
    <Fragment>
      <Box sx={{ width: '100%' }} className="space-y-3">
        <Box sx={{ ...siteAuditModalSx.secondBoxSx, overflow: 'auto' }}>
          <Stepper
            activeStep={activeStep}
            connector={<QontoConnector />}
            sx={{ display: { ns: 'flex' } }}
          >
            {steps.map((label, index) => {
              return (
                <Step key={label} sx={siteAuditModalSx.step}>
                  <StepLabel StepIconComponent={ColorlibStepIcon}>
                    <Typography sx={siteAuditModalSx.stepLabel}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        <Typography
          className="flex-col my-3 space-y-3 mb-0"
          sx={siteAuditModalSx.guideText}
        >
          {getGuideText()}
        </Typography>
        {getAuditModalSectionHtml()}
        <Grid container className="justify-end mt-0" spacing={1} mb={5}>
          <Grid item xs={12}>
            <Button
              variant="contained"
              disabled={
                loading ||
                buttonDisable ||
                (activeStep === 1 && isButtonDisable)
              }
              fullWidth={true}
              style={{ height: 40 }}
              onClick={() => handleNext()}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : activeStep === steps.length - 1 ? (
                'Start Audit'
              ) : activeStep === 2 ? (
                // googleAuthText?.success ? (
                gscStatus || googleAuthText?.success ? (
                  'Next Step'
                ) : (
                  'Watch Tutorial'
                )
              ) : (
                'Next Step'
              )}
            </Button>
          </Grid>
          <Grid item xs={12} className="text-center">
            {activeStep !== 3 && (
              <Links
                sx={{ cursor: 'pointer' }}
                underline="always"
                onClick={() => handleNextSkip()}
              >
                {runSkipBtn}
              </Links>
            )}
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}></Box>
      </Box>

      <CommonModal
        open={openSkipModal}
        handCloseModal={() => {
          setOpenSkipModal(true);
        }}
        title=""
        closebtn={false}
        blur={false}
      >
        <SkipSiteAuditModal
          handleCloseSkipModal={handleCloseSkipModal}
          handleSkipModal={handleSkipModal}
        />
      </CommonModal>
    </Fragment>
  );
};

export default SiteAuditModal;
